/**
 * Equipe — member list. A phone gets stacked cards; a desktop keeps the
 * Slack-style table. Add lives in the page on a phone so the dark header
 * stays back-and-title, like Settings.
 */
import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { IoSearchOutline } from 'react-icons/io5';
import { BASE_TEAM_SEATS, FREE_INCLUDED_SEATS, PORTAL_PATHS } from '@x9/billing';
import PortalShell from '../components/PortalShell';
import PortalLoader from '../components/PortalLoader';
import UserChip from '../components/UserChip';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { cn } from '../lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { getFirebaseAuth, getFirebaseDb } from '../config/firebase';
import { assignSeat, unassignSeat, callableErrorMessage, type SeatSlot } from '../services/api';
import { useBillingSummary } from '../hooks/useBillingSummary';

type SeatPlan = {
  packs?: number;
  slots?: SeatSlot[];
  occupied?: number;
};

const ERROR_COPY: Record<string, string> = {
  no_packs: 'Sua conta ainda não tem pacotes de assentos. Assine um plano para começar.',
  no_capacity: 'Todos os assentos estão ocupados. Adquira mais um pacote de 5 assentos.',
  reassign_limit: 'Este assento já foi reatribuído neste ciclo. Aguarde o próximo ciclo ou adquira mais assentos.',
  already_seated: 'Essa pessoa já ocupa um assento nesta conta.',
  self_seat: 'Você não precisa de um assento para si — o uso pessoal é ilimitado. Assentos são para outras pessoas da equipe.',
  invalid_phone: 'Não foi possível entender esse número de telefone.',
};

function friendlyError(e: unknown): string {
  const raw = callableErrorMessage(e);
  for (const [key, copy] of Object.entries(ERROR_COPY)) {
    if (raw.includes(key)) return copy;
  }
  return raw;
}

function hasX9Account(slot: SeatSlot): boolean {
  return !!slot.occupant?.uid;
}

function AccountBadge({ registered }: { registered: boolean }) {
  return (
    <Badge variant="outline" className="whitespace-nowrap">
      <span
        className={cn(
          'h-1.5 w-1.5 shrink-0 rounded-full',
          registered ? 'bg-emerald-500' : 'bg-amber-500',
        )}
        aria-hidden
      />
      {registered ? 'Conta válida' : 'Sem conta ainda'}
    </Badge>
  );
}

function AddFields({
  name,
  phone,
  busy,
  onName,
  onPhone,
  onSubmit,
  onCancel,
}: {
  name: string;
  phone: string;
  busy: boolean;
  onName: (value: string) => void;
  onPhone: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const canSave = !busy && phone.replace(/\D/g, '').length >= 10;
  return (
    <div className="flex flex-col gap-2 sm:grid sm:grid-cols-2 sm:gap-3">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="seat-name">Nome</Label>
        <Input
          id="seat-name"
          placeholder="Nome completo"
          value={name}
          onChange={(e) => onName(e.target.value)}
          className="h-10"
          disabled={busy}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="seat-phone">Telefone</Label>
        <Input
          id="seat-phone"
          inputMode="tel"
          placeholder="11 91234-5678"
          value={phone}
          onChange={(e) => onPhone(e.target.value)}
          className="h-10"
          disabled={busy}
          autoFocus
        />
      </div>
      <p className="text-xs text-muted-foreground sm:col-span-2">
        Validamos a conta X9 ao salvar.
      </p>
      <div className="flex flex-row flex-wrap items-center gap-2 pt-1 sm:col-span-2">
        <Button
          width="auto"
          size="sm"
          className="w-auto"
          disabled={!canSave}
          onClick={onSubmit}
        >
          {busy ? '…' : 'Salvar'}
        </Button>
        <Button
          variant="ghost"
          width="auto"
          size="sm"
          className="w-auto"
          disabled={busy}
          onClick={onCancel}
        >
          Cancelar
        </Button>
      </div>
    </div>
  );
}

export default function SeatsPage({ onNavigate }: { onNavigate: (path: string) => void }) {
  const { summary } = useBillingSummary(true);
  const [plan, setPlan] = useState<SeatPlan | null>(null);
  const [ready, setReady] = useState(false);
  const [adding, setAdding] = useState(false);
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const uid = getFirebaseAuth().currentUser?.uid;
    if (!uid) return;
    const unsub = onSnapshot(doc(getFirebaseDb(), 'seatPlans', uid), (snap) => {
      setPlan((snap.data() as SeatPlan) || null);
      setReady(true);
    });
    return unsub;
  }, []);

  const hasPaid = summary?.hasPaidAccess === true;
  const slots = plan?.slots || [];
  const occupiedSlots = slots.filter((s) => s.occupant);
  const capacity = hasPaid
    ? Math.max(slots.length, BASE_TEAM_SEATS)
    : FREE_INCLUDED_SEATS;
  const freeSeats = Math.max(0, capacity - occupiedSlots.length);
  const atLimit = freeSeats <= 0;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return occupiedSlots;
    return occupiedSlots.filter((s) => {
      const occ = s.occupant!;
      const hay = `${occ.displayName || ''} ${occ.phoneDisplay} ${occ.phoneE164}`.toLowerCase();
      return hay.includes(q);
    });
  }, [occupiedSlots, query]);

  const resetAdd = () => {
    setAdding(false);
    setPhone('');
    setName('');
    setError(null);
  };

  const startAdd = () => {
    if (atLimit) return;
    setError(null);
    setAdding(true);
  };

  const submit = async () => {
    setError(null);
    setBusy(true);
    try {
      await assignSeat(phone.trim(), name.trim() || undefined);
      setPhone('');
      setName('');
      const remaining = freeSeats - 1;
      if (remaining <= 0) setAdding(false);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (phoneE164: string) => {
    setError(null);
    setBusy(true);
    try {
      await unassignSeat(phoneE164);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const memberLabel =
    occupiedSlots.length === 1
      ? '1 membro'
      : `${occupiedSlots.length} membros`;

  const addAction = !atLimit ? (
    <Button width="auto" size="sm" className="w-auto" onClick={startAdd} disabled={adding}>
      Adicionar
    </Button>
  ) : (
    <Button
      variant="secondary"
      width="auto"
      size="sm"
      className="w-auto"
      onClick={() => onNavigate(PORTAL_PATHS.ACCOUNT)}
    >
      Ampliar plano
    </Button>
  );

  const emptyCopy = query.trim()
    ? 'Nenhum membro corresponde ao filtro.'
    : 'Nenhuma pessoa na equipe ainda.';

  return (
    <PortalShell
      active="seats"
      onNavigate={onNavigate}
      title="Equipe"
      backHref="/settings"
      titleAction={addAction}
    >
      <div className="flex flex-col gap-3 lg:gap-5">
      {!ready && <PortalLoader />}

      {ready && !hasPaid && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm sm:text-base">
              Plano gratuito inclui 1 assento
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Seu uso pessoal é ilimitado. Você pode atribuir rotinas a uma outra pessoa. Para
              mais gente, o plano Equipe começa em {BASE_TEAM_SEATS} assentos.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              width="auto"
              size="sm"
              className="w-auto"
              onClick={() => onNavigate(PORTAL_PATHS.ACCOUNT)}
            >
              Conhecer os planos
            </Button>
          </CardContent>
        </Card>
      )}

      {ready && (
        <>
          <div className="flex items-center justify-between gap-3">
            <p className="min-w-0 text-sm text-muted-foreground">
              {memberLabel}
              <span> · {occupiedSlots.length} de {capacity} assentos</span>
            </p>
            <div className="shrink-0 lg:hidden">{addAction}</div>
          </div>
          {occupiedSlots.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Atribuir uma rotina no app também preenche o próximo assento.
            </p>
          ) : null}

          {occupiedSlots.length > 0 ? (
            <div className="relative">
              <IoSearchOutline
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filtrar por nome ou telefone…"
                className="h-10 py-2 pl-9 text-sm"
              />
            </div>
          ) : null}

          {adding ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Adicionar pessoa</CardTitle>
              </CardHeader>
              <CardContent>
                <AddFields
                  name={name}
                  phone={phone}
                  busy={busy}
                  onName={setName}
                  onPhone={setPhone}
                  onSubmit={() => void submit()}
                  onCancel={resetAdd}
                />
              </CardContent>
            </Card>
          ) : null}

          <div className="flex flex-col gap-2 lg:hidden">
            {filtered.map((s) => {
              const occ = s.occupant!;
              const registered = hasX9Account(s);
              const label = occ.displayName || occ.phoneDisplay;
              return (
                <Card key={occ.phoneE164}>
                  <CardContent className="flex flex-col gap-2 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <UserChip displayName={label} photoURL={null} />
                      <Button
                        variant="ghost"
                        width="auto"
                        size="sm"
                        className="w-auto shrink-0 text-destructive"
                        disabled={busy}
                        onClick={() => void remove(occ.phoneE164)}
                      >
                        Remover
                      </Button>
                    </div>
                    <p className="text-sm tabular-nums text-muted-foreground">
                      {occ.phoneDisplay}
                    </p>
                    <AccountBadge registered={registered} />
                  </CardContent>
                </Card>
              );
            })}
            {filtered.length === 0 && !adding ? (
              <p className="py-6 text-center text-sm text-muted-foreground">{emptyCopy}</p>
            ) : null}
          </div>

          <div className="hidden lg:block">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Nome</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead>Conta X9</TableHead>
                <TableHead className="text-right"> </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((s) => {
                const occ = s.occupant!;
                const registered = hasX9Account(s);
                const label = occ.displayName || occ.phoneDisplay;
                return (
                  <TableRow key={occ.phoneE164}>
                    <TableCell>
                      <UserChip displayName={label} photoURL={null} />
                    </TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">
                      {occ.phoneDisplay}
                    </TableCell>
                    <TableCell>
                      <AccountBadge registered={registered} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        width="auto"
                        size="sm"
                        className="w-auto"
                        disabled={busy}
                        onClick={() => void remove(occ.phoneE164)}
                      >
                        Remover
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}

              {filtered.length === 0 && !adding && (
                <TableRow>
                  <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                    {emptyCopy}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </>
      )}
      </div>
    </PortalShell>
  );
}
